// START IMPORTS
// Vendor imports
import googleTagManager, {
  GoogleTagManagerConfig,
} from '@analytics/google-tag-manager';
import { initialize } from '@ltv-apm-modules/react';
// Core lib imports
import { version, CookiesStorage } from '@ltvco/refresh-lib';
import { SubscriptionAccessConfigProvider } from '@ltvco/refresh-lib/ctx';
import { TosModalLegalError } from '@ltvco/refresh-lib/payments';
import { ErrorBoundary, styled } from '@ltvco/refresh-lib/theme';
import {
  CancelFinalStepModal,
  closeSnackbar,
  ConfirmCancelModal,
  ContactOptionsModal,
  invalidateSession,
  isUserLoggedIn,
  type SearchData,
  SessionProvider,
  useSession,
} from '@ltvco/refresh-lib/v1';
import {
  BannerMigrationCta,
  Footer,
  SearchFormProvider,
} from '@ltvco/refresh-lib/v2';
// Vendor imports continued
import Analytics from 'analytics';
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';

// App-specific imports
import { AppContainer } from 'components/AppContainer';
import { EmailVerification } from 'components/EmailVerification';
import { Growthbook } from 'components/Growthbook';
import { HeaderParent } from 'components/HeaderParent/HeaderParent';
import { LibContextWrapper } from 'components/LibContextWrapper';
import QueryWrapper from 'components/queryWrapper/QueryWrapper';
import { Recycling } from 'components/recycling/Recycling';
import { generateFooterLinks } from 'utils/footerLinks/footerLinks';
import { goToLogin } from 'utils/goToLogin';

import packageInfo from '../package.json';

import { AppRoutes as Routes } from './AppRoutes';

// Type imports
import type { ResponseError, ErrorRequestData } from '@ltvco/refresh-lib/utils';

// Asset imports
import 'App.css';
// END IMPORTS

// START CONSTANTS
let otel: any = null;
if (
  import.meta.env.VITE_TARGET_ENV === 'production' ||
  import.meta.env.VITE_TARGET_ENV === 'staging'
) {
  otel = initialize({
    name: 'refresh-peoplelooker',
    url: import.meta.env.VITE_APM_URL,
    apikey: import.meta.env.VITE_APM_KEY,
  });
}

declare global {
  interface Window {
    env: Record<string, string>;
  }
}
// END CONSTANTS

// START STYLED_COMPONENTS
const HeaderContainer = styled('div')`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 20;
`;

const MigrationBannerContainer = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
}));

const LibVersion = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(2)} 0 0 0`,
  fontSize: theme.typography.fontSize * 0.75,
}));
// END STYLED_COMPONENTS

function App() {
  // START HOOKS_AND_QUERIES
  const analytics = Analytics({
    app: 'refresh-beenverified',
    debug: true,
    plugins: [
      googleTagManager({
        containerId: 'GTM-WRLQV7',
        dataLayer: (window as any)?.dataLayer ?? [],
      } as GoogleTagManagerConfig),
    ],
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useSession();
  const [isCoreLoginRedirect] = useQueryParam(
    'is_core_login_redirect',
    StringParam
  );
  const [loginType] = useQueryParam('login_type', StringParam);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [isAuthenticated, setIsAuthenticated] = useState(isUserLoggedIn());
  const [isContactOptionsModalOpen, setIsContactOptionsModalOpen] =
    useState(false);
  const [isConfirmCancelModalOpen, setIsConfirmCancelModalOpen] =
    useState(false);
  const [isCancelFinalStepModalOpen, setIsCancelFinalStepModalOpen] =
    useState(false);
  const [showCheaperPlanLink, setShowCheaperPlanLink] = useState(false);
  const [_isDownsellSuccessModalVisible, setIsDownsellSuccessModalVisible] =
    useState<boolean>(false);

  const [tosState, setTosState] = useState<{
    modalOpen: boolean;
    requestData: ErrorRequestData['body'] | null;
  }>({
    modalOpen: false,
    requestData: null,
  });
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const storage = new CookiesStorage();
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    window.history.scrollRestoration = 'manual';
  }, []);

  useEffect(() => {
    if (
      isCoreLoginRedirect === 'true' &&
      !sessionStorage.getItem('has_tracked_core_login_redirect')
    ) {
      analytics.track('site_event', {
        event_name: 'login_attempt',
        type: loginType || 'Web',
        success: true,
      });

      sessionStorage.setItem(
        'has_tracked_core_login_redirect',
        isCoreLoginRedirect
      );
    }
  }, [isCoreLoginRedirect]);
  // END EFFECTS

  // START EVENT_HANDLERS
  async function handleLogoutClick(ga4Ttracker?: () => void) {
    await invalidateSession();
    setIsAuthenticated(isUserLoggedIn());
    closeSnackbar();
    storage.setValue('partialLocView', 'false');
    goToLogin('https://www.peoplelooker.com');
    if (ga4Ttracker) {
      ga4Ttracker();
    }
  }

  function isResponseError(error: any): error is ResponseError {
    return error.response !== undefined;
  }

  function logError(
    context: string,
    error: Error | ResponseError,
    url: string | undefined
  ) {
    const isProdEnv = import.meta.env.VITE_TARGET_ENV === 'production';
    if (!isProdEnv || !otel) return;

    const { account } = session;
    const { api } = otel;
    if (api.isOTELInitialized()) {
      if (account) {
        api.setUser({
          id: account?.account.user_info.user_code,
          email: account?.account.user_info.email,
        });
      }
      let errorMsg = `${context}: ${error.message}, Cause: ${error.cause}`;
      if (isResponseError(error)) {
        errorMsg = `${errorMsg}, Status: ${
          error.response.status
        }, Data: ${JSON.stringify(error.response)}`;
      }
      if (url) {
        errorMsg += `, URL: ${url}`;
      }
      api.pushError(new Error(errorMsg));
    }
  }

  function trackEventGA4(eventToTrack: object, eventName?: string) {
    const targetEnv = import.meta.env.VITE_TARGET_ENV;
    if (targetEnv === 'dev') return;
    analytics?.track(eventName || 'site_event', eventToTrack);
  }

  function trackEvent(category: string, action: string, label?: string) {
    analytics.track('ua_event', {
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
    });
  }

  const onLoginSuccess = () => {
    let next = '/';

    if (location.state && location.state.next) {
      next = location.state.next;
    }

    navigate(
      next === '/' ||
        (next.startsWith('/report') && !next.includes('permalink'))
        ? '/dashboard'
        : next
    );
    setIsAuthenticated(isUserLoggedIn());
  };

  const contactUsHandler = () => {
    setIsContactOptionsModalOpen(true);
  };

  const openDownsellSuccessModal = () => {
    setIsDownsellSuccessModalVisible(true);
  };

  const handleSearchSuccess = ({
    reportType,
    searchParams,
    permalink,
  }: SearchData): void => {
    const url = `/report/${reportType}?${searchParams}&permalink=${permalink}`;
    navigate(url);
  };
  const openConfirmCancelModal = (downsellTest?: boolean) => {
    setIsConfirmCancelModalOpen(true);
    setShowCheaperPlanLink(downsellTest || false);
  };

  const onCancelConfirmCancelModal = () => {
    setIsConfirmCancelModalOpen(false);
  };
  // END EVENT_HANDLERS
  // START RENDER
  return (
    <ErrorBoundary
      redirectFunction={() => {
        navigate('/error');
      }}
      location={location}
      logFunction={logError as any}
      trackEventFunction={trackEventGA4}
    >
      <QueryWrapper
        handleLogout={handleLogoutClick}
        trackEventGA4={trackEventGA4}
        setTosState={setTosState}
      >
        <SessionProvider isAuthenticated={isAuthenticated}>
          <SubscriptionAccessConfigProvider>
            <Growthbook>
              <LibContextWrapper
                trackEventGA4={trackEventGA4}
                logError={logError}
                trackEvent={trackEvent}
                handleLogoutClick={handleLogoutClick}
              >
                <EmailVerification>
                  <SearchFormProvider>
                    <div className="app">
                      <HeaderContainer>
                        <MigrationBannerContainer>
                          <BannerMigrationCta />
                        </MigrationBannerContainer>
                        <HeaderParent
                          handleSearchSuccess={handleSearchSuccess}
                        />
                      </HeaderContainer>
                      <AppContainer>
                        <Routes
                          handleLogoutClick={handleLogoutClick}
                          handleSearchSuccess={handleSearchSuccess}
                          contactUsHandler={contactUsHandler}
                          onLoginSuccess={onLoginSuccess}
                          openConfirmCancelModal={openConfirmCancelModal}
                          onCancelConfirmCancelModal={
                            onCancelConfirmCancelModal
                          }
                        />
                        <Recycling isAuthenticated={isAuthenticated} />
                      </AppContainer>
                      <ContactOptionsModal
                        isOpen={isContactOptionsModalOpen}
                        setIsOpen={setIsContactOptionsModalOpen}
                        openConfirmCancelModal={(downsellTest?: boolean) => {
                          setIsConfirmCancelModalOpen(true);
                          setShowCheaperPlanLink(downsellTest || false);
                        }}
                      />
                      <ConfirmCancelModal
                        isOpen={isConfirmCancelModalOpen}
                        onClose={() => setIsConfirmCancelModalOpen(false)}
                        onCancel={() => {
                          setIsConfirmCancelModalOpen(false);
                          setIsCancelFinalStepModalOpen(true);
                        }}
                        openSuccessModal={openDownsellSuccessModal}
                        showDownsell={true}
                        showCheaperPlanLink={showCheaperPlanLink}
                      />
                      <CancelFinalStepModal
                        isOpen={isCancelFinalStepModalOpen}
                        onClose={() => setIsCancelFinalStepModalOpen(false)}
                      />
                      <TosModalLegalError
                        tosState={tosState}
                        setTosState={setTosState}
                        handleSearchSuccess={handleSearchSuccess}
                      />

                      <Footer
                        footerLinks={generateFooterLinks({
                          contactUsHandler,
                        })}
                        libVersion={
                          <LibVersion>
                            {import.meta.env.VITE_TARGET_ENV ===
                            'production' ? (
                              <code>{version}</code>
                            ) : (
                              <code>
                                {packageInfo.dependencies['@ltvco/refresh-lib']}
                              </code>
                            )}
                          </LibVersion>
                        }
                        openConfirmCancelModal={openConfirmCancelModal}
                        hasMobileApps
                      />
                    </div>
                  </SearchFormProvider>
                </EmailVerification>
              </LibContextWrapper>
            </Growthbook>
          </SubscriptionAccessConfigProvider>
        </SessionProvider>
      </QueryWrapper>
    </ErrorBoundary>
  );
  // END RENDER
}

export default App;
